import { DragEvent, ReactNode, useRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as XIcon } from '@/assets/icons/x.svg';
import { Label } from '@/components/Forms';
import {
  Dropzone,
  InnerLabel,
  Notice,
  Title,
} from '@/components/Forms/FileField';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import Text from '@/components/Text';

const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
`;

const RemoveFileButton = styled.button`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

/**
 * Does a file satisfy one entry of an `accept` list? Handles the three forms
 * the attribute allows: `.ext`, `type/subtype` and `type/*`.
 */
const matchesAcceptEntry = (file: File, entry: string) => {
  if (entry.startsWith('.')) {
    return file.name.toLowerCase().endsWith(entry.toLowerCase());
  }
  if (entry.endsWith('/*')) {
    return file.type.startsWith(entry.slice(0, -1));
  }
  return file.type === entry;
};

/**
 * `accept` only filters the OS picker, so dropped files have to be checked
 * here too. An empty/absent list accepts everything.
 */
const matchesAccept = (file: File, accept?: string) => {
  const entries = (accept ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
  return (
    entries.length === 0 || entries.some((e) => matchesAcceptEntry(file, e))
  );
};

const rejectionMessage = (tooLarge: File[], wrongType: File[]) => {
  const names = (files: File[]) => files.map((f) => f.name).join(', ');
  const parts: string[] = [];
  if (tooLarge.length) parts.push(`${names(tooLarge)} exceeded the size limit`);
  if (wrongType.length)
    parts.push(`${names(wrongType)} is not a supported type`);
  return `${parts.join('; ')} — not added`;
};

interface Props {
  value: File[];
  onChange: (files: File[]) => void;
  label?: ReactNode;
  /** Same syntax as the input attribute; also enforced on dropped files. */
  accept?: string;
  /** Max bytes per file. Omit for no limit. */
  maxSize?: number;
  /** Hint under the drop prompt, e.g. "PDF, doc / up to 5MB each". */
  notes?: ReactNode;
  className?: string;
}

/**
 * Drop zone collecting several files into plain `File[]` state — for uploads
 * sent as raw form data rather than stored through react-hook-form.
 */
const MultipleFileField = ({
  value,
  onChange,
  label,
  accept,
  maxSize,
  notes,
  className,
}: Props) => {
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Single funnel for both the picker and the drop, so they can't diverge.
  const addFiles = (list: FileList | null) => {
    if (!list?.length) return;
    const picked = Array.from(list);
    const tooLarge = picked.filter(
      (f) => maxSize !== undefined && f.size > maxSize,
    );
    const wrongType = picked.filter(
      (f) => !tooLarge.includes(f) && !matchesAccept(f, accept),
    );
    const accepted = picked.filter(
      (f) => !tooLarge.includes(f) && !wrongType.includes(f),
    );

    setError(
      tooLarge.length || wrongType.length
        ? rejectionMessage(tooLarge, wrongType)
        : null,
    );
    if (accepted.length) onChange([...value, ...accepted]);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.items?.length) setDragging(true);
  };

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    addFiles(e.dataTransfer?.files ?? null);
    e.dataTransfer?.clearData();
  };

  return (
    <div className={className}>
      {!!label && <Label>{label}</Label>}
      {/* Kept outside the zone so its synthetic click can't bubble back into
          the zone's own onClick and reopen the picker. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        accept={accept}
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = '';
        }}
      />
      <Dropzone
        $dragging={dragging}
        onClick={() => inputRef.current?.click()}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        style={{ cursor: 'pointer' }}
      >
        <Title $type="smallBold" $color="gray" as="div">
          Drop files here or{' '}
          <InnerLabel $type="smallBold" $color="purple" as="span">
            browse
          </InnerLabel>
        </Title>
        {!!notes && (
          <Notice $type="extraSmall" $color="gray" as="div">
            {notes}
          </Notice>
        )}
      </Dropzone>
      {!!error && <ValidationMessage>{error}</ValidationMessage>}
      {value.length > 0 && (
        <FilesList>
          {value.map((file, index) => (
            <FileItem key={index}>
              <Text $type="smallMedium">{file.name}</Text>
              <RemoveFileButton
                type="button"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
              >
                <XIcon />
              </RemoveFileButton>
            </FileItem>
          ))}
        </FilesList>
      )}
    </div>
  );
};

export default MultipleFileField;
